<template>
  <section class="finance-page" :dir="pageDirection">
    <div v-if="loading" class="page-loading">
      <div class="loader-card">
        <div class="loader-ring"></div>

        <h3>{{ tr("loadingTitle") }}</h3>

        <p>{{ tr("loadingText") }}</p>
      </div>
    </div>

    <div class="page-header">
      <h1>{{ tr("pageTitle") }}</h1>

      <p>{{ tr("pageSubtitle") }}</p>
    </div>

    <div class="top-row">
      <div class="counter">5 {{ tr("indicators") }}</div>

      <select>
        <option>{{ tr("pageTitle") }}</option>
      </select>
    </div>

    <div class="axis-summary">
      <div class="axis-card-header">
        <div>
          <h2>{{ tr("axisTitle") }}</h2>

          <p>{{ tr("axisMeta") }}</p>

          <strong>{{ tr("axisTagline") }}</strong>
        </div>

        <div class="axis-score">
          <span> {{ axisAverage !== null ? `${axisAverage}%` : "-" }}</span>

          <small>
            {{ tr("fisWeight") }}:

            {{ axisWeight !== null ? `${axisWeight}%` : "-" }}</small
          >
        </div>
      </div>

      <div class="axis-stats">
        <div>
          <b>{{ financeAchievedCount }}</b>

          <span>{{ tr("achieved") }}</span>
        </div>

        <div>
          <b>{{ financeBelowStandardCount }}</b>

          <span>{{ tr("belowStandard") }}</span>
        </div>

        <div>
          <b>{{ financeCriticalCount }}</b>

          <span>{{ tr("critical") }}</span>
        </div>
      </div>
    </div>

    <div class="axis-indicator-grid">
      <article
        v-for="kpi in financeKpis"
        :key="kpi.code"
        class="axis-indicator-card"
        :class="{ flag: kpiStatus(kpi) === 'r' }"
        :data-critical-label="tr('criticalFlag')"
      >
        <div class="axis-indicator-top">
          <span class="code">{{ kpi.code }}</span>

          <span v-if="kpi.composite" class="composite">{{
            tr("composite")
          }}</span>
        </div>

        <h3>{{ kpi.name }}</h3>

        <div class="axis-indicator-value">
          <strong :style="{ color: statusColor(kpi) }">{{ kpi.val }}</strong>

          <span>{{ kpi.unit }}</span>

          <em
            >{{ tr("standard") }} {{ kpi.dir === "lt" ? "≤" : "≥" }}

            {{ kpi.std }}</em
          >
        </div>

        <div class="gauge">
          <div
            :style="{
              width: barWidth(kpi) + '%',

              background: statusColor(kpi),
            }"
          ></div>
        </div>

        <div class="axis-indicator-foot">
          <span>
            {{ tr("achievement") }}:

            <b :style="{ color: statusColor(kpi) }">{{ achieve(kpi) }}%</b>
          </span>

          <span :class="['rag', kpiStatus(kpi)]">
            {{ statusLabel[kpiStatus(kpi)] }}
          </span>
        </div>

        <div v-if="kpi.chart" class="axis-mini-chart">
          <h4>{{ kpi.chart.label }}</h4>

          <apexchart
            v-if="chartsReady"
            :type="kpiApex(kpi).type"
            height="220"
            :series="kpiApex(kpi).series"
            :options="kpiApex(kpi).options"
          />
        </div>
      </article>
    </div>

    <!-- <div class="axis-card">
      <div class="axis-title">
        <div>
          <span class="dot"></span>

          <strong>{{ tr("dashboardTitle") }}</strong>

          <small>{{ tr("dashboardMeta") }}</small>
        </div>
      </div>

      <p class="desc">{{ tr("dashboardDescription") }}</p>

      <div class="info-box">
        <p>
          <strong>{{ tr("monthlyOperatingGap") }}:</strong>

          27 مليون ريال ≈ $17,881 ·

          <strong>{{ tr("highestGap") }}:</strong>

          مستشفى الصداقة ·

          <strong>{{ tr("salaryDelay") }}:</strong>

          موثَّق ·

          <strong>{{ tr("patientFees") }}:</strong>

          تفرضها معظم المرافق ·

          <strong>{{ tr("vulnerableExemptions") }}:</strong>

          نعم
        </p>
      </div>

      <div class="kpi-grid">
        <div class="kpi-card yellow">
          <div class="kpi-value">{{ avgRequiredBudget }}2</div>

          <div class="kpi-label">{{ tr("avgRequiredBudget") }}</div>
        </div>

        <div class="kpi-card green">
          <div class="kpi-value">{{ avgAvailableBudget }}</div>

          <div class="kpi-label">{{ tr("avgAvailableBudget") }}</div>
        </div>

        <div class="kpi-card orange">
          <div class="kpi-value">{{ avgCoverage }}%</div>

          <div class="kpi-label">{{ tr("avgCoverage") }}</div>
        </div>

        <div class="kpi-card red">
          <div class="kpi-value">{{ avgGap }}%</div>

          <div class="kpi-label">{{ tr("avgGap") }}</div>
        </div>
      </div>

      <div class="charts-grid">
        <div class="chart-card">
          <div class="section-title">{{ tr("mainFundingSources") }}</div>

          <apexchart
            v-if="chartsReady"
            type="donut"
            height="320"
            :series="fundingSourceSeries"
            :options="donutOptions(fundingSourceLabels)"
          />
        </div>

        <div class="chart-card">
          <div class="section-title">{{ tr("mostAffectedAreas") }}</div>

          <apexchart
            v-if="chartsReady"
            type="bar"
            height="320"
            :series="[{ name: tr('facilityCount'), data: affectedAreaSeries }]"
            :options="barOptions(affectedAreaLabels)"
          />
        </div>

        <div class="chart-card chart-wide">
          <div class="section-title">{{ tr("externalFundingNeeds") }}</div>

          <apexchart
            v-if="chartsReady"
            type="bar"
            height="320"
            :series="[{ name: tr('facilityCount'), data: externalNeedSeries }]"
            :options="barOptions(externalNeedLabels)"
          />
        </div>
      </div>

      <div class="table-wrap mb-table">
        <div class="table-title">
          {{ tr("financeDetails") }}

          <span>{{ finances.length }} {{ tr("facilityUnit") }}</span>
        </div>

        <table class="finance-table">
          <thead>
            <tr>
              <th>{{ tr("facility") }}</th>

              <th>{{ tr("requiredBudget") }}</th>

              <th>{{ tr("availableBudget") }}</th>

              <th>{{ tr("coveragePercent") }}</th>

              <th>{{ tr("gapPercent") }}</th>

              <th>{{ tr("fundingSources") }}</th>

              <th>{{ tr("mostAffected") }}</th>

              <th>{{ tr("externalNeed") }}</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="item in finances" :key="item.id">
              <td class="name">{{ item.facility_name }}</td>

              <td>{{ formatMoney(item.required_monthly_budget) }}</td>

              <td>{{ formatMoney(item.available_monthly_budget) }}</td>

              <td>
                <span
                  :class="['status-badge', coverageClass(coverageRate(item))]"
                >
                  {{ coverageRate(item) }}%
                </span>
              </td>

              <td>
                <span :class="['status-badge', gapClass(gapRate(item))]">
                  {{ gapRate(item) }}%
                </span>
              </td>

              <td>{{ item.main_funding_sources_label }}</td>

              <td>{{ item.most_affected_areas_label }}</td>

              <td>{{ item.external_funding_needs_label }}</td>
            </tr>

            <tr v-if="!loading && !finances.length">
              <td colspan="8" class="empty-cell">{{ tr("noFinanceData") }}</td>
            </tr>
          </tbody>
        </table>

        <div class="mobile-finance-cards">
          <article
            v-for="item in finances"
            :key="`mobile-${item.id}`"
            class="mobile-finance-card"
          >
            <div class="mobile-card-head">
              <h3>{{ item.facility_name }}</h3>

              <span
                :class="['status-badge', coverageClass(coverageRate(item))]"
              >
                {{ coverageRate(item) }}%
              </span>
            </div>

            <div class="mobile-card-body">
              <div class="mobile-data-row">
                <span>{{ tr("requiredBudget") }}</span
                ><strong>{{
                  formatMoney(item.required_monthly_budget)
                }}</strong>
              </div>

              <div class="mobile-data-row">
                <span>{{ tr("availableBudget") }}</span
                ><strong>{{
                  formatMoney(item.available_monthly_budget)
                }}</strong>
              </div>

              <div class="mobile-data-row">
                <span>{{ tr("coveragePercent") }}</span
                ><strong>{{ coverageRate(item) }}%</strong>
              </div>

              <div class="mobile-data-row">
                <span>{{ tr("gapPercent") }}</span
                ><strong>{{ gapRate(item) }}%</strong>
              </div>

              <div class="mobile-data-row text-row">
                <span>{{ tr("fundingSources") }}</span
                ><strong>{{ item.main_funding_sources_label }}</strong>
              </div>

              <div class="mobile-data-row text-row">
                <span>{{ tr("mostAffected") }}</span
                ><strong>{{ item.most_affected_areas_label }}</strong>
              </div>

              <div class="mobile-data-row text-row">
                <span>{{ tr("externalNeed") }}</span
                ><strong>{{ item.external_funding_needs_label }}</strong>
              </div>
            </div>
          </article>

          <div v-if="!loading && !finances.length" class="mobile-empty">
            {{ tr("noFinanceData") }}
          </div>
        </div>
      </div>
    </div> -->
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";

import { useI18n } from "vue-i18n";

import api from "@/plugins/api";

const { t, locale } = useI18n();

const pageDirection = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));

const isArabic = computed(() => locale.value === "ar");

const tr = (key, params = {}) => t(`financePage.${key}`, params);

const loading = ref(false);

const chartsReady = ref(false);

const finances = ref([]);

const fin03Sources = ref([]);

const fin01 = ref(0);

const fin02 = ref(0);

const fin03 = ref(0);

const fin05 = ref(0);

const statusLabel = computed(() => ({
  g: tr("compliant"),

  a: tr("near"),

  r: tr("gap"),
}));

const colors = { g: "#10b981", a: "#f59e0b", r: "#ef4444" };

const normalizeArray = (payload) => {
  if (Array.isArray(payload)) return payload;

  if (Array.isArray(payload?.data)) return payload.data;

  if (Array.isArray(payload?.data?.data)) return payload.data.data;

  if (Array.isArray(payload?.results)) return payload.results;

  return [];
};

const getApiNumber = (res) => {
  const d = res?.data;

  return Number(
    d?.value ??
      d?.average ??
      d?.avg ??
      d?.score ??
      d?.rate ??
      d?.percentage ??
      d?.summary?.value ??
      d?.summary?.average ??
      d?.summary?.avg ??
      d?.summary?.score ??
      d?.summary?.rate ??
      d?.summary?.percentage ??
      d?.summary?.FIN_01 ??
      d?.summary?.FIN_02 ??
      d?.summary?.FIN_03 ??
      d?.summary?.FIN_05 ??
      d?.data?.value ??
      d?.data?.average ??
      d?.data?.avg ??
      d?.data?.score ??
      d?.data?.rate ??
      d?.data?.percentage ??
      0
  );
};

const cleanValue = (value) => {
  if (value === null || value === undefined || value === "")
    return tr("unspecified");

  return String(value);
};

const fundingSourceMap = computed(() => ({
  government: tr("values.government"),

  patient_fees: tr("values.patientFees"),

  donors: tr("values.donors"),

  organizations: tr("values.organizations"),

  community: tr("values.community"),

  insurance: tr("values.insurance"),

  none: tr("values.none"),

  "غير محدد": tr("unspecified"),
}));

const affectedAreaMap = computed(() => ({
  salaries: tr("values.salaries"),

  medicines: tr("values.medicines"),

  supplies: tr("values.supplies"),

  maintenance: tr("values.maintenance"),

  electricity: tr("values.electricity"),

  fuel: tr("values.fuel"),

  water: tr("values.water"),

  referrals: tr("values.referrals"),

  operations: tr("values.operations"),

  none: tr("values.none"),

  "غير محدد": tr("unspecified"),
}));

const externalNeedMap = computed(() => ({
  medicines: tr("values.medicines"),

  equipment: tr("values.equipment"),

  maintenance: tr("values.maintenance"),

  infrastructure: tr("values.infrastructure"),

  solar: tr("values.solar"),

  water_sanitation: tr("values.waterSanitation"),

  salaries: tr("values.salaries"),

  training: tr("values.training"),

  none: tr("values.none"),

  "غير محدد": tr("unspecified"),
}));

const translateMulti = (value, map) => {
  const key = cleanValue(value);

  if (key === tr("unspecified")) return key;

  return key

    .split(",")

    .map((item) => {
      const cleanItem = item.trim();

      return map[cleanItem] || cleanItem;
    })

    .join("، ");
};

const fetchFinances = async () => {
  try {
    loading.value = true;

    chartsReady.value = false;

    const [fin05Res, fin01Res, fin02Res, fin03Res, financeRes, facilitiesRes] =
      await Promise.all([
        api.get("/health-facilities/financialResilienceIndex"),

        api.get("/health-facilities/financialCoverageRate"),

        api.get("/health-facilities/fundingGapRate"),

        api.get("/health-facilities/fundingSourcesDiversity"),

        api.get("/facility-finances"),

        api.get("/health-facilities"),
      ]);

    fin05.value = getApiNumber(fin05Res);

    fin01.value = getApiNumber(fin01Res);

    fin02.value = getApiNumber(fin02Res);

    fin03.value = getApiNumber(fin03Res);

    fin03Sources.value = (fin03Res.data?.by_sources || []).map((item) => ({
      label: item.source,

      value: Number(item.count_facilities || 0),
    }));

    const financeList = normalizeArray(financeRes.data);

    const facilitiesList = normalizeArray(facilitiesRes.data);

    const facilityMap = {};

    facilitiesList.forEach((facility, index) => {
      const surveyId = String(facility.survey_id || facility.id || index + 1);

      facilityMap[surveyId] = {
        ar:
          facility.facility_name_ar ||
          facility.facility_name ||
          facility.name ||
          `${tr("facilityFallback")} #${surveyId}`,

        en:
          facility.facility_name_en ||
          facility.facility_name_ar ||
          facility.facility_name ||
          facility.name ||
          `${tr("facilityFallback")} #${surveyId}`,
      };
    });

    finances.value = financeList.map((item) => {
      const surveyId = String(item.survey_id);

      const required = Number(
        item.required_monthly_budget ||
          item.monthly_required_budget ||
          item.required_budget ||
          0
      );

      const available = Number(
        item.available_monthly_budget ||
          item.monthly_available_budget ||
          item.available_budget ||
          0
      );

      return {
        id: item.id,

        survey_id: item.survey_id,

        facility_name:
          (isArabic.value
            ? facilityMap[surveyId]?.ar
            : facilityMap[surveyId]?.en) ||
          (isArabic.value
            ? item.facility_name_ar || item.facility_name
            : item.facility_name_en ||
              item.facility_name_ar ||
              item.facility_name) ||
          `${tr("surveyFallback")} #${item.survey_id}`,

        required_monthly_budget: required,

        available_monthly_budget: available,

        main_funding_sources: cleanValue(
          item.main_funding_sources || item.funding_sources
        ),

        main_funding_sources_label: translateMulti(
          item.main_funding_sources || item.funding_sources,

          fundingSourceMap.value
        ),

        most_affected_areas: cleanValue(
          item.most_affected_areas || item.affected_areas
        ),

        most_affected_areas_label: translateMulti(
          item.most_affected_areas || item.affected_areas,

          affectedAreaMap.value
        ),

        external_funding_needs: cleanValue(
          item.external_funding_needs || item.external_needs
        ),

        external_funding_needs_label: translateMulti(
          item.external_funding_needs || item.external_needs,

          externalNeedMap.value
        ),
      };
    });
  } catch (error) {
    console.error("Facility Finances API Error:", error);

    finances.value = [];
  } finally {
    chartsReady.value = true;

    loading.value = false;
  }
};

const axisWeight = ref(null);

const axisAverage = ref(null);

const loadAxisWeight = async () => {
  try {
    const response = await api.get("/health-facilities/axis-averages");

    const payload = response.data?.data;

    axisWeight.value = payload?.finance?.weight ?? null;

    axisAverage.value = payload?.finance?.average ?? null;
  } catch (error) {
    console.error("خطأ في تحميل بيانات المحاور:", error);

    axisWeight.value = null;

    axisAverage.value = null;
  }
};

onMounted(async () => {
  await Promise.allSettled([fetchFinances(), loadAxisWeight()]);
});

const coverageRate = (item) => {
  if (!item.required_monthly_budget) return 0;

  return Number(
    (
      (item.available_monthly_budget / item.required_monthly_budget) *
      100
    ).toFixed(1)
  );
};

const avgCoverage = computed(() => {
  if (!finances.value.length) return 0;

  const total = finances.value.reduce(
    (sum, item) => sum + Number(coverageRate(item) || 0),

    0
  );

  return Number((total / finances.value.length).toFixed(1));
});

const avgGap = computed(() =>
  Number((100 - Number(avgCoverage.value || 0)).toFixed(1))
);

const fin04Local = computed(() => {
  if (!finances.value.length) return 0;

  const externalCount = finances.value.filter((item) => {
    const sources = String(item.main_funding_sources || "");

    return sources.includes("donors") || sources.includes("organizations");
  }).length;

  return Number(((externalCount / finances.value.length) * 100).toFixed(1));
});

const financeCharts = computed(() => ({
  "FIN-01": {
    type: "radialBar",

    label: tr("charts.avgCoverage"),

    value: Number(fin01.value || avgCoverage.value || 0),
  },

  "FIN-02": {
    type: "radialBar",

    label: tr("charts.avgFundingGap"),

    value: Number(fin02.value || avgGap.value || 0),
  },

  "FIN-03": {
    type: "donut",

    label: tr("charts.fundingDistribution"),

    data: fin03Sources.value.map((x) => x.value),

    labels: fin03Sources.value.map((x) => x.label),
  },

  "FIN-05": {
    type: "bar",

    horizontal: true,

    label: tr("charts.hffriComponents"),

    data: [
      Number(fin01.value || 0) * 0.4,

      (100 - Number(fin02.value || 0)) * 0.25,

      Number(fin03.value || 0) * 0.2,

      (100 - Number(fin04Local.value || 0)) * 0.15,
    ],

    labels: [
      tr("components.coverage"),

      tr("components.inverseGap"),

      tr("components.diversity"),

      tr("components.sustainability"),
    ],
  },
}));

const financeKpis = computed(() => [
  {
    code: "FIN-01",

    name: tr("kpis.coverage"),

    val: Number(fin01.value || avgCoverage.value || 0),

    unit: "%",

    std: 80,

    dir: "gt",

    max: 100,

    composite: true,

    flag: Number(fin01.value || 0) < 50,

    chart: financeCharts.value["FIN-01"],
  },

  {
    code: "FIN-02",

    name: tr("kpis.gap"),

    val: Number(fin02.value || avgGap.value || 0),

    unit: "%",

    std: 50,

    dir: "lt",

    max: 100,

    composite: true,

    flag: Number(fin02.value || 0) >= 50,

    chart: financeCharts.value["FIN-02"],
  },

  {
    code: "FIN-03",

    name: tr("kpis.diversity"),

    val: Number(fin03.value || 0),

    unit: "/100",

    std: 75,

    dir: "gt",

    max: 100,

    composite: true,

    flag: Number(fin03.value || 0) < 75,

    chart: financeCharts.value["FIN-03"],
  },

  {
    code: "FIN-05",

    name: tr("kpis.resilience"),

    val: Number(fin05.value || 0),

    unit: "/100",

    std: 70,

    dir: "gt",

    max: 100,

    composite: true,

    flag: Number(fin05.value || 0) < 60,

    chart: financeCharts.value["FIN-05"],
  },
]);

const financeAchievedCount = computed(
  () => financeKpis.value.filter((k) => kpiStatus(k) === "g").length
);

const financeBelowStandardCount = computed(
  () => financeKpis.value.filter((k) => kpiStatus(k) === "a").length
);

const financeCriticalCount = computed(
  () => financeKpis.value.filter((k) => kpiStatus(k) === "r").length
);

const kpiStatus = (kpi) => {
  const v = Number(kpi.val || 0);

  const std = Number(kpi.std || 0);

  if (kpi.dir === "lt") {
    if (v <= std) return "g";

    // حرج عندما تتجاوز القيمة المعيار بنسبة 30% أو أكثر.
    if (v < std * 1.3) return "a";

    return "r";
  }

  if (v >= std) return "g";

  // حرج عندما تقل القيمة عن المعيار بنسبة 30% أو أكثر.
  if (v > std * 0.7) return "a";

  return "r";
};

const statusColor = (kpi) => colors[kpiStatus(kpi)];

const achieve = (kpi) => {
  const v = Number(kpi.val || 0);

  const std = Number(kpi.std || 1);

  const max = Number(kpi.max || 100);

  if (kpi.dir === "lt") {
    return v <= std
      ? 100
      : Math.max(
          0,

          Math.round(100 - ((v - std) / Math.max(1, max - std)) * 100)
        );
  }

  return Math.min(100, Math.round((v / std) * 100));
};

const barWidth = (kpi) =>
  Math.min(
    100,

    Math.max(3, (Number(kpi.val || 0) / Number(kpi.max || 100)) * 100)
  );

const miniBaseChart = {
  chart: {
    toolbar: { show: false },

    fontFamily: isArabic.value ? "Cairo" : "Inter",

    foreColor: "#7890ad",
  },

  dataLabels: { enabled: false },

  legend: { position: "bottom" },

  grid: { borderColor: "#d7e0eb" },

  noData: { text: tr("noData") },
};

const miniBarOptions = (labels = [], horizontal = false) => ({
  ...miniBaseChart,

  plotOptions: {
    bar: { horizontal, borderRadius: 6, columnWidth: "45%" },
  },

  xaxis: {
    categories: labels.length ? labels : [tr("noData")],

    labels: { rotate: horizontal ? 0 : -35, trim: true },
  },
});

const miniDonutOptions = (labels = []) => ({
  chart: {
    type: "donut",

    toolbar: { show: false },

    fontFamily: isArabic.value ? "Cairo" : "Inter",
  },

  labels,

  colors: ["#0ea5e9", "#14b8a6", "#8b5cf6", "#f59e0b", "#ec4899"],

  stroke: {
    width: 0,
  },

  plotOptions: {
    pie: {
      donut: {
        size: "62%",

        labels: {
          show: false,
        },
      },
    },
  },

  dataLabels: {
    enabled: false,
  },

  legend: {
    show: true,

    position: "bottom",

    horizontalAlign: "center",

    fontSize: "12px",

    floating: false,

    itemMargin: {
      horizontal: 5,

      vertical: 2,
    },

    markers: {
      width: 10,

      height: 10,

      radius: 10,
    },
  },

  tooltip: {
    y: {
      formatter: (val) => `${val} ${tr("facilityUnit")}`,
    },
  },
});

const radialOptions = (value) => ({
  ...miniBaseChart,

  chart: {
    type: "radialBar",

    toolbar: { show: false },

    fontFamily: isArabic.value ? "Cairo" : "Inter",
  },

  plotOptions: {
    radialBar: {
      startAngle: -135,

      endAngle: 135,

      hollow: { size: "68%" },

      track: { background: "#e2e8f0" },

      dataLabels: {
        name: { show: false },

        value: {
          fontSize: "34px",

          fontWeight: 900,

          color: value >= 80 ? "#10b981" : value >= 50 ? "#f59e0b" : "#ef4444",

          formatter: (val) => `${Number(val).toFixed(1)}%`,
        },
      },
    },
  },

  colors: [value >= 80 ? "#10b981" : value >= 50 ? "#f59e0b" : "#ef4444"],
});

const kpiApex = (kpi) => {
  const ch = kpi.chart;

  if (!ch) {
    return { type: "bar", series: [], options: miniBarOptions([]) };
  }

  if (ch.type === "radialBar") {
    const value = Number(ch.value || 0);

    return {
      type: "radialBar",

      series: [value],

      options: radialOptions(value),
    };
  }

  if (ch.type === "donut") {
    return {
      type: "donut",

      series: ch.data?.length ? ch.data : [0],

      options: miniDonutOptions(ch.labels || []),
    };
  }

  return {
    type: "bar",

    series: [{ name: kpi.code, data: ch.data?.length ? ch.data : [0] }],

    options: miniBarOptions(ch.labels || [], !!ch.horizontal),
  };
};
</script>

<style scoped>
.finance-page {
  min-height: 100vh;

  background: #f4f6f9;

  padding: 24px;

  color: #071126;

  font-family: "Inter", "Cairo", sans-serif;
}

.page-loading {
  position: fixed;

  inset: 0;

  background: rgba(244, 246, 249, 0.92);

  backdrop-filter: blur(6px);

  z-index: 9999;

  display: flex;

  align-items: center;

  justify-content: center;
}

.loader-card {
  width: 320px;

  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 22px;

  padding: 34px 24px;

  text-align: center;

  box-shadow: 0 20px 60px rgba(15, 35, 64, 0.15);
}

.loader-ring {
  width: 64px;

  height: 64px;

  border: 6px solid #e2e8f0;

  border-top-color: #ca8a04;

  border-radius: 50%;

  margin: 0 auto 18px;

  animation: spin 0.8s linear infinite;
}

.loader-card h3 {
  font-size: 18px;

  font-weight: 900;

  color: #ca8a04;

  margin-bottom: 8px;
}

.loader-card p {
  color: #64748b;

  font-size: 13px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.page-header {
  text-align: right;

  margin-bottom: 26px;
}

.page-header h1 {
  font-size: 28px;

  font-weight: 900;

  margin-bottom: 8px;

  color: #061226;
}

.page-header p {
  color: #52657d;

  font-size: 14px;
}

.top-row {
  display: flex;

  align-items: center;

  justify-content: space-between;

  margin-bottom: 18px;
}

.counter {
  color: #64748b;

  font-size: 14px;
}

.top-row select {
  width: 225px;

  height: 50px;

  background: #fff;

  border: 1px solid #cbd5e1;

  border-radius: 8px;

  padding: 0 14px;

  font-family: inherit;
}

.axis-summary,
.measured-box {
  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 18px;

  padding: 18px;

  margin-bottom: 18px;

  box-shadow: 0 8px 24px rgba(15, 35, 64, 0.05);
}

.axis-card-header {
  display: flex;

  justify-content: space-between;

  gap: 20px;

  align-items: center;
}

.axis-card-header h2 {
  font-size: 22px;

  font-weight: 900;

  color: #ca8a04;

  margin-bottom: 6px;
}

.axis-card-header p {
  color: #64748b;

  font-size: 13px;

  margin-bottom: 8px;
}

.axis-card-header strong {
  color: #071126;

  font-size: 14px;
}

.axis-score {
  min-width: 130px;

  text-align: center;

  background: #fef9c3;

  color: #854d0e;

  border-radius: 18px;

  padding: 18px;
}

.axis-score span {
  display: block;

  font-size: 34px;

  font-weight: 900;
}

.axis-score small {
  font-weight: 800;
}

.axis-stats {
  display: grid;

  grid-template-columns: repeat(3, 1fr);

  gap: 12px;

  margin-top: 16px;
}

.axis-stats div {
  background: #f8fafc;

  border: 1px solid #e2e8f0;

  border-radius: 14px;

  padding: 14px;

  text-align: center;
}

.axis-stats b {
  display: block;

  font-size: 26px;

  color: #ca8a04;
}

.axis-stats span {
  font-size: 13px;

  font-weight: 800;

  color: #64748b;
}

.measured-grid {
  display: grid;

  grid-template-columns: repeat(2, 1fr);

  gap: 14px;
}

.measure-card {
  background: #f8fafc;

  border: 1px solid #e2e8f0;

  border-radius: 14px;

  padding: 15px;
}

.measure-card span {
  display: block;

  min-height: 42px;

  font-size: 13px;

  font-weight: 800;

  color: #334155;
}

.measure-card b {
  display: block;

  font-size: 30px;

  font-weight: 900;

  color: #ca8a04;

  margin: 8px 0;
}

.measure-card small,
.section-sub {
  color: #64748b;

  font-weight: 700;
}

.section-sub {
  font-size: 13px;

  margin-bottom: 14px;
}

.axis-card {
  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 12px;

  padding: 22px;

  box-shadow: 0 1px 4px rgba(15, 35, 64, 0.05);
}

.axis-title {
  border-bottom: 1px solid #dbe3ee;

  padding-bottom: 14px;

  margin-bottom: 16px;
}

.axis-title > div {
  display: flex;

  align-items: center;

  gap: 8px;

  flex-wrap: wrap;
}

.dot {
  width: 12px;

  height: 12px;

  background: #ca8a04;

  border-radius: 4px;
}

.axis-title strong {
  font-size: 18px;

  font-weight: 900;
}

.axis-title small {
  font-size: 12px;

  color: #64748b;
}

.desc {
  color: #64748b;

  font-size: 14px;

  margin-bottom: 14px;
}

.info-box {
  background: #f8fafc;

  border: 1px solid #dbe3ee;

  border-radius: 12px;

  padding: 16px 18px;

  margin-bottom: 12px;

  line-height: 2;

  font-size: 13px;

  color: #334155;
}

.info-box strong {
  color: #ca8a04;
}

.kpi-grid {
  display: grid;

  grid-template-columns: repeat(4, 1fr);

  gap: 14px;

  margin: 18px 0;
}

.kpi-card {
  border-radius: 16px;

  padding: 20px;

  text-align: center;

  border: 1px solid #dbe3ee;
}

.kpi-value {
  font-size: 25px;

  font-weight: 900;
}

.kpi-label {
  margin-top: 8px;

  font-size: 13px;

  font-weight: 800;
}

.kpi-card.yellow {
  background: #fef9c3;

  color: #854d0e;
}

.kpi-card.green {
  background: #e8f5e9;

  color: #1b5e20;
}

.kpi-card.orange {
  background: #fff4e5;

  color: #d4821a;
}

.kpi-card.red {
  background: #fdecea;

  color: #c62828;
}

.charts-grid {
  display: grid;

  grid-template-columns: repeat(2, 1fr);

  gap: 14px;

  margin: 18px 0;
}

.chart-card {
  background: #f8fafc;

  border: 1px solid #dbe3ee;

  border-radius: 12px;

  padding: 16px;

  min-height: 370px;
}

.chart-wide {
  grid-column: span 2;
}

.section-title {
  font-size: 16px;

  font-weight: 900;

  color: #ca8a04;

  margin-bottom: 12px;
}

.table-wrap {
  overflow-x: auto;

  border: 1px solid #dbe3ee;

  border-radius: 10px;

  background: #fff;
}

.mb-table,
.indicators-table {
  margin-bottom: 18px;
}

.table-title {
  background: #ca8a04;

  color: #fff;

  padding: 14px 18px;

  font-weight: 800;

  display: flex;

  justify-content: space-between;

  align-items: center;
}

.table-title span {
  background: rgba(255, 255, 255, 0.15);

  padding: 4px 10px;

  border-radius: 999px;

  font-size: 12px;
}

table {
  width: 100%;

  min-width: 1100px;

  border-collapse: collapse;

  background: #fff;
}

.finance-table {
  min-width: 1200px;
}

.indicators-table table {
  min-width: 1300px;
}

th {
  background: #f8fafc;

  color: #ca8a04;

  font-size: 13px;

  font-weight: 900;

  padding: 15px 14px;

  text-align: right;

  white-space: wrap;

  border-bottom: 1px solid #e2e8f0;
}

td {
  padding: 14px;

  border-bottom: 1px solid #dbe3ee;

  font-size: 13px;

  line-height: 1.8;

  vertical-align: top;
}

tbody tr:hover {
  background: #f8fafc;
}

.code {
  font-weight: 900;

  direction: ltr;

  text-align: center;

  white-space: wrap;

  color: #ca8a04;
}

.name {
  font-weight: 900;

  min-width: 220px;
}

.unit {
  text-align: center;

  white-space: wrap;
}

.status-badge {
  display: inline-flex;

  align-items: center;

  gap: 7px;

  border-radius: 999px;

  padding: 7px 13px;

  font-size: 12px;

  font-weight: 700;
}

.status-green {
  background: #dcfce7;

  color: #166534;
}

.status-orange {
  background: #ffedd5;

  color: #9a3412;
}

.status-red {
  background: #fee2e2;

  color: #991b1b;
}

.empty-cell {
  text-align: center;

  color: #64748b;

  padding: 20px !important;
}

@media (max-width: 1000px) {
  .charts-grid {
    grid-template-columns: 1fr;
  }

  .chart-wide {
    grid-column: span 1;
  }

  .kpi-grid,
  .measured-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .axis-card-header {
    flex-direction: column;

    align-items: stretch;
  }
}

@media (max-width: 900px) {
  .finance-page {
    padding: 16px 12px;
  }

  .page-header h1 {
    font-size: 21px;
  }

  .top-row {
    flex-direction: column-reverse;

    align-items: stretch;

    gap: 12px;
  }

  .top-row select {
    width: 100%;
  }

  .axis-card {
    padding: 14px;
  }

  .axis-stats {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .kpi-grid,
  .measured-grid {
    grid-template-columns: 1fr;
  }
}

/* ===== Indicator mini cards copied from axis indicators page ===== */

.axis-indicator-grid {
  display: grid;

  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));

  gap: 14px;

  margin-bottom: 18px;
}

.axis-indicator-card {
  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 16px;

  padding: 16px;

  position: relative;

  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
}

.axis-indicator-card.flag {
  border-color: rgba(239, 68, 68, 0.38);
}

.axis-indicator-card.flag::after {
  content: attr(data-critical-label);

  position: absolute;

  top: 13px;

  left: 14px;

  font-size: 10px;

  color: #ef4444;

  font-weight: 900;
}

.axis-indicator-top {
  display: flex;

  gap: 8px;

  margin-bottom: 10px;
}

.axis-indicator-card h3 {
  font-size: 15px;

  font-weight: 900;

  color: #071126;

  margin: 0 0 12px;

  min-height: 42px;
}

.axis-indicator-value {
  display: flex;

  align-items: baseline;

  gap: 7px;

  margin-bottom: 10px;
}

.axis-indicator-value strong {
  font-size: 32px;

  font-weight: 900;

  direction: ltr;
}

.axis-indicator-value span {
  color: #64748b;
}

.axis-indicator-value em {
  margin-right: auto;

  font-style: normal;

  background: #f8fafc;

  border: 1px solid #e2e8f0;

  border-radius: 8px;

  padding: 5px 9px;

  color: #475569;

  font-size: 11px;

  font-weight: 800;
}

.axis-indicator-foot {
  display: flex;

  justify-content: space-between;

  align-items: center;

  margin: 8px 0 13px;

  font-size: 12px;

  font-weight: 700;

  color: #64748b;
}

.axis-mini-chart {
  background: #f8fafc;

  border: 1px solid #dbe3ee;

  border-radius: 14px;

  padding: 14px;
}

.axis-mini-chart h4 {
  font-size: 14px;

  font-weight: 900;

  color: inherit;

  margin: 0 0 10px;
}

.axis-mini-chart {
  overflow: hidden;
}

.axis-mini-chart .apexcharts-legend {
  max-height: none !important;

  overflow: visible !important;

  max-height: 125px !important;
}

.composite {
  background: #f5f3ff;

  border: 1px solid #ddd6fe;

  color: #7c3aed;

  border-radius: 7px;

  padding: 3px 8px;

  font-size: 10px;

  font-weight: 900;
}

.gauge {
  height: 9px;

  background: #e2e8f0;

  border-radius: 999px;

  overflow: hidden;
}

.gauge div {
  height: 100%;

  border-radius: 999px;
}

.rag {
  border-radius: 7px;

  padding: 4px 10px;

  font-weight: 900;
}

.rag.g {
  background: rgba(16, 185, 129, 0.12);

  color: #10b981;
}

.rag.a {
  background: rgba(245, 158, 11, 0.14);

  color: #f59e0b;
}

.rag.r {
  background: rgba(239, 68, 68, 0.12);

  color: #ef4444;
}

/* ================= RTL / LTR ================= */

.finance-page[dir="rtl"] {
  direction: rtl;

  font-family: "Cairo", sans-serif;
}

.finance-page[dir="ltr"] {
  direction: ltr;

  font-family: "Inter", "Cairo", sans-serif;
}

.finance-page[dir="rtl"] .page-header,
.finance-page[dir="rtl"] th,
.finance-page[dir="rtl"] td,
.finance-page[dir="rtl"] .section-title {
  text-align: right;
}

.finance-page[dir="ltr"] .page-header,
.finance-page[dir="ltr"] th,
.finance-page[dir="ltr"] td,
.finance-page[dir="ltr"] .section-title {
  text-align: left;
}

.finance-page[dir="ltr"] .axis-indicator-card.flag::after {
  left: auto;

  right: 14px;
}

.finance-page[dir="rtl"] .axis-indicator-card.flag::after {
  right: auto;

  left: 14px;
}

.finance-page[dir="ltr"] .axis-indicator-value em {
  margin-left: auto;

  margin-right: 0;
}

.finance-page[dir="rtl"] .axis-indicator-value em {
  margin-right: auto;

  margin-left: 0;
}

.mobile-finance-cards {
  display: none;
}

@media (max-width: 768px) {
  .finance-page {
    padding: 12px 8px 28px;

    overflow-x: hidden;
  }

  .page-header {
    margin-bottom: 16px;
  }

  .page-header h1 {
    font-size: 20px;

    line-height: 1.5;
  }

  .page-header p {
    font-size: 12px;

    line-height: 1.8;
  }

  .axis-summary {
    padding: 13px;

    border-radius: 15px;
  }

  .axis-card-header {
    flex-direction: column;

    align-items: stretch;

    gap: 12px;
  }

  .axis-card-header h2 {
    font-size: 18px;
  }

  .axis-score {
    width: 100%;

    min-width: 0;

    padding: 13px;

    border-radius: 14px;
  }

  .axis-score span {
    font-size: 28px;
  }

  .axis-stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));

    gap: 7px;
  }

  .axis-stats div {
    padding: 10px 5px;

    border-radius: 11px;
  }

  .axis-stats b {
    font-size: 21px;
  }

  .axis-stats span {
    font-size: 10px;

    line-height: 1.5;
  }

  .axis-indicator-grid {
    grid-template-columns: minmax(0, 1fr);

    gap: 12px;
  }

  .axis-indicator-card {
    padding: 13px;
  }

  .axis-indicator-card h3 {
    min-height: 0;

    font-size: 14px;
  }

  .axis-indicator-value {
    flex-wrap: wrap;
  }

  .axis-indicator-value strong {
    font-size: 28px;
  }

  .axis-indicator-value em {
    width: 100%;

    margin-inline: 0;

    text-align: center;
  }

  .axis-mini-chart {
    padding: 8px;

    overflow-x: auto;

    -webkit-overflow-scrolling: touch;
  }

  .axis-mini-chart :deep(.apexcharts-canvas) {
    max-width: 100% !important;
  }

  .axis-card {
    padding: 12px 8px;
  }

  .kpi-grid,
  .measured-grid,
  .charts-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .chart-wide {
    grid-column: span 1;
  }

  .chart-card {
    padding: 12px;

    min-height: 330px;

    overflow: hidden;
  }

  .finance-table {
    display: none;
  }

  .table-wrap {
    overflow: visible;

    border: 0;

    background: transparent;
  }

  .table-title {
    padding: 12px 13px;

    border-radius: 12px;

    font-size: 14px;
  }

  .mobile-finance-cards {
    display: grid;

    gap: 12px;

    margin-top: 12px;
  }

  .mobile-finance-card {
    padding: 14px;

    background: #fff;

    border: 1px solid #dbe3ee;

    border-inline-start: 5px solid #ca8a04;

    border-radius: 15px;

    box-shadow: 0 6px 18px rgba(15, 35, 64, 0.06);
  }

  .mobile-card-head {
    display: flex;

    align-items: flex-start;

    justify-content: space-between;

    gap: 10px;

    padding-bottom: 12px;

    margin-bottom: 4px;

    border-bottom: 1px solid #e2e8f0;
  }

  .mobile-card-head h3 {
    min-width: 0;

    margin: 0;

    color: #854d0e;

    font-size: 15px;

    font-weight: 900;

    line-height: 1.7;

    overflow-wrap: break-word;
  }

  .mobile-card-head .status-badge {
    flex-shrink: 0;
  }

  .mobile-card-body {
    display: grid;
  }

  .mobile-data-row {
    display: flex;

    align-items: flex-start;

    justify-content: space-between;

    gap: 14px;

    padding: 10px 0;

    border-bottom: 1px dashed #e2e8f0;
  }

  .mobile-data-row:last-child {
    border-bottom: 0;
  }

  .mobile-data-row span {
    flex: 0 0 42%;

    color: #64748b;

    font-size: 12px;

    font-weight: 800;
  }

  .mobile-data-row strong {
    min-width: 0;

    color: #071126;

    font-size: 14px;

    font-weight: 900;

    text-align: end;

    overflow-wrap: break-word;
  }

  .mobile-data-row:not(.text-row) strong {
    direction: ltr;
  }

  .mobile-empty {
    padding: 24px;

    text-align: center;

    color: #64748b;

    background: #fff;

    border: 1px dashed #cbd5e1;

    border-radius: 14px;

    font-weight: 800;
  }

  .loader-card {
    width: calc(100% - 28px);

    padding: 28px 18px;
  }
}

@media (max-width: 380px) {
  .axis-stats {
    grid-template-columns: 1fr;
  }

  .mobile-card-head {
    flex-direction: column;
  }
}
</style>
